import { useEffect, useState } from "react";
import { Loader2, Search, X } from "@fg/icons";
import { connectParityApi } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import { useCreateConversation } from "@/api/hooks";
import { cn } from "@/lib/utils";

interface DirectoryUser {
    userId: string;
    email: string;
    displayName: string | null;
    tenantId: string;
}

/**
 * Multi-recipient picker for starting a new group conversation.
 * Mirrors Teams' "New chat → comma-separated recipients" form.
 * Backed by the existing directory search endpoint which can resolve
 * by name or email substring.
 */
export function NewGroupDialog({
    onClose,
    onCreated
}: {
    onClose: () => void;
    onCreated: (conversationId: string) => void;
}) {
    const token = useAccessToken();
    const create = useCreateConversation();
    const [name, setName] = useState("");
    const [query, setQuery] = useState("");
    const [results, setResults] = useState<DirectoryUser[]>([]);
    const [picked, setPicked] = useState<DirectoryUser[]>([]);
    const [searching, setSearching] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Debounced directory search
    useEffect(() => {
        const q = query.trim();
        if (q.length < 2) {
            setResults([]);
            return;
        }
        let cancelled = false;
        setSearching(true);
        const handle = window.setTimeout(async () => {
            try {
                const r = await connectParityApi.searchDirectory(q, token);
                if (!cancelled) setResults(r);
            } catch (e) {
                if (!cancelled) setError(e instanceof Error ? e.message : String(e));
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 200);
        return () => {
            cancelled = true;
            window.clearTimeout(handle);
        };
    }, [query, token]);

    const togglePick = (u: DirectoryUser) => {
        setPicked((prev) => {
            if (prev.some((p) => p.userId === u.userId)) {
                return prev.filter((p) => p.userId !== u.userId);
            }
            return [...prev, u];
        });
    };

    const submit = async () => {
        if (picked.length < 2) {
            setError("Pick at least two people for a group chat.");
            return;
        }
        setError(null);
        try {
            const conv = await create.mutateAsync({
                name: name.trim() || undefined,
                participants: picked.map((p) => ({
                    userId: p.userId,
                    email: p.email,
                    displayName: p.displayName ?? undefined
                }))
            });
            onCreated(conv.id);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    };

    return (
        <div
            role="dialog"
            aria-label="New group chat"
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
            onClick={(e) => { if (e.target === e.currentTarget && !create.isPending) onClose(); }}
        >
            <div className="flex w-full max-w-md flex-col rounded-lg border bg-card p-4 shadow-2xl">
                <div className="mb-2 flex items-center justify-between">
                    <h2 className="text-base font-semibold">New group chat</h2>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close"
                        disabled={create.isPending}
                        className="rounded p-1 hover:bg-accent disabled:opacity-50"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>
                <p className="mb-3 text-xs text-muted-foreground">
                    Pick two or more people. Optionally name the group so the roster can find it later.
                </p>

                <label className="mb-1 block text-xs font-medium">Group name (optional)</label>
                <input
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Project Alpha"
                    className="mb-3 w-full rounded-md border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                />

                <label className="mb-1 block text-xs font-medium">Add people</label>
                <div className="relative mb-2">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <input
                        value={query}
                        onChange={(e) => setQuery(e.target.value)}
                        placeholder="Type a name or email"
                        className="w-full rounded-md border bg-background pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                    />
                </div>

                {picked.length > 0 ? (
                    <div className="mb-2 flex flex-wrap gap-1">
                        {picked.map((p) => (
                            <span
                                key={p.userId}
                                className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary"
                            >
                                {p.displayName ?? p.email}
                                <button
                                    type="button"
                                    onClick={() => togglePick(p)}
                                    aria-label={`Remove ${p.displayName ?? p.email}`}
                                    className="rounded-full hover:bg-primary/20"
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                ) : null}

                <div className="max-h-56 overflow-auto rounded-md border">
                    {searching ? (
                        <p className="flex items-center gap-2 p-3 text-xs text-muted-foreground">
                            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Searching…
                        </p>
                    ) : results.length === 0 && query.trim().length >= 2 ? (
                        <p className="p-3 text-xs text-muted-foreground">No matches.</p>
                    ) : results.length === 0 ? (
                        <p className="p-3 text-xs text-muted-foreground">Start typing to search the directory.</p>
                    ) : (
                        <ul role="list" className="divide-y">
                            {results.map((u) => {
                                const selected = picked.some((p) => p.userId === u.userId);
                                return (
                                    <li key={u.userId}>
                                        <button
                                            type="button"
                                            onClick={() => togglePick(u)}
                                            className={cn(
                                                "flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent",
                                                selected ? "bg-primary/5" : null
                                            )}
                                        >
                                            <input type="checkbox" checked={selected} readOnly className="h-3.5 w-3.5" />
                                            <div className="min-w-0 flex-1">
                                                <div className="truncate text-sm font-medium">
                                                    {u.displayName ?? u.email}
                                                </div>
                                                {u.displayName ? (
                                                    <div className="truncate text-xs text-muted-foreground">{u.email}</div>
                                                ) : null}
                                            </div>
                                        </button>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {error ? (
                    <p className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
                        {error}
                    </p>
                ) : null}

                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={create.isPending}
                        className="rounded-md border px-3 py-1.5 text-sm hover:bg-accent disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={() => void submit()}
                        disabled={picked.length < 2 || create.isPending}
                        className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                    >
                        {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
                        Create group ({picked.length})
                    </button>
                </div>
            </div>
        </div>
    );
}
